/**
 * FinAppLayout.jsx — Layout de FinApp (tema claro, estilo propio)
 * Diseño basado en mockup oficial: sidebar blanco, acentos índigo/púrpura
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutGrid,
  Building2,
  CreditCard,
  ArrowLeftRight,
  PieChart,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wallet
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/finanzas',              icon: LayoutGrid,       label: 'Panel',          exact: true },
  { path: '/finanzas/entidades',    icon: Building2,        label: 'Entidades' },
  { path: '/finanzas/cuentas',      icon: CreditCard,       label: 'Cuentas' },
  { path: '/finanzas/movimientos',  icon: ArrowLeftRight,   label: 'Movimientos' },
  { path: '/finanzas/resumen',      icon: PieChart,         label: 'Resumen' },
  { path: '/finanzas/configuracion',icon: Settings,         label: 'Configuración' },
];

const PURPLE = '#6C5ECF';
const PURPLE_LIGHT = '#F3F0FF';
const SIDEBAR_BG = '#FFFFFF';
const SIDEBAR_BORDER = '#E8E8ED';
const TEXT_DARK = '#1A1A2E';
const TEXT_MID = '#6B7280';
const TEXT_LIGHT = '#9CA3AF';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const FinAppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const handleClose = () => window.close();
  const handleLogout = () => { logout(); navigate('/login'); };

  const activeItem = NAV_ITEMS.find(i =>
    i.exact ? location.pathname === i.path : location.pathname.startsWith(i.path)
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8F8FB', fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ═══════ SIDEBAR ═══════ */}
      <aside
        style={{
          width: collapsed ? '60px' : '210px',
          flexShrink: 0,
          background: SIDEBAR_BG,
          borderRight: `1px solid ${SIDEBAR_BORDER}`,
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          height: '100vh',
          transition: 'width 0.25s ease',
          zIndex: 50,
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
        }}
        data-testid="finapp-sidebar"
      >
        {/* ── Logo ── */}
        <div style={{
          padding: collapsed ? '20px 0' : '20px 20px 16px',
          borderBottom: `1px solid ${SIDEBAR_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          minHeight: '72px',
        }}>
          <div style={{
            width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
            background: `linear-gradient(135deg, ${PURPLE} 0%, #8B7CF8 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 12px rgba(108,94,207,0.3)`,
            marginLeft: collapsed ? 'auto' : '0',
            marginRight: collapsed ? 'auto' : '0',
          }}>
            <Wallet size={16} color="#fff" strokeWidth={2} />
          </div>
          {!collapsed && (
            <div>
              <p style={{ fontSize: '15px', fontWeight: 700, color: TEXT_DARK, lineHeight: 1.2, letterSpacing: '-0.01em' }}>FinApp</p>
              <p style={{ fontSize: '9px', fontWeight: 600, color: TEXT_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '2px' }}>Gestión Financiera</p>
            </div>
          )}
        </div>

        {/* ── Nav Items ── */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`finapp-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                title={collapsed ? item.label : ''}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: collapsed ? '10px 0' : '9px 12px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  background: isActive ? PURPLE_LIGHT : 'transparent',
                  color: isActive ? PURPLE : TEXT_MID,
                  fontWeight: isActive ? 600 : 400,
                  fontSize: '13.5px',
                  position: 'relative',
                  borderLeft: isActive && !collapsed ? `3px solid ${PURPLE}` : '3px solid transparent',
                  paddingLeft: isActive && !collapsed ? '10px' : '12px',
                }}
                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = '#F9F8FF'; e.currentTarget.style.color = TEXT_DARK; } }}
                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEXT_MID; } }}
              >
                <Icon size={15} strokeWidth={isActive ? 2 : 1.75} />
                {!collapsed && (
                  <>
                    <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                    {isActive && <ChevronRight size={13} style={{ color: PURPLE, opacity: 0.6 }} />}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        {/* ── User + Logout ── */}
        <div style={{ borderTop: `1px solid ${SIDEBAR_BORDER}`, padding: collapsed ? '12px 0' : '14px 16px' }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={{
                width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${PURPLE} 0%, #8B7CF8 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: 700, color: '#fff',
              }}>
                {getInitials(user?.full_name || user?.email)}
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT_DARK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.full_name || user?.email?.split('@')[0]}
                </p>
                <p style={{ fontSize: '10.5px', color: TEXT_LIGHT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                  {user?.email}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={handleClose}
            data-testid="finapp-close-btn"
            title={collapsed ? 'Cerrar FinApp' : ''}
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              gap: '8px', padding: collapsed ? '8px 0' : '8px 10px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: '7px', border: 'none', cursor: 'pointer',
              background: 'transparent', color: TEXT_MID, fontSize: '12.5px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#FFF0F0'; e.currentTarget.style.color = '#EF4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = TEXT_MID; }}
          >
            <LogOut size={14} strokeWidth={1.75} />
            {!collapsed && <span>Cerrar Sesión</span>}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          data-testid="finapp-collapse-btn"
          style={{
            position: 'absolute', right: '-12px', top: '50%', transform: 'translateY(-50%)',
            width: '24px', height: '24px', borderRadius: '50%',
            background: SIDEBAR_BG, border: `1px solid ${SIDEBAR_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            color: TEXT_MID, zIndex: 10,
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = PURPLE_LIGHT; e.currentTarget.style.borderColor = PURPLE; e.currentTarget.style.color = PURPLE; }}
          onMouseLeave={e => { e.currentTarget.style.background = SIDEBAR_BG; e.currentTarget.style.borderColor = SIDEBAR_BORDER; e.currentTarget.style.color = TEXT_MID; }}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>

      {/* ═══════ MAIN CONTENT ═══════ */}
      <main
        style={{
          flex: 1,
          marginLeft: collapsed ? '60px' : '210px',
          transition: 'margin-left 0.25s ease',
          minHeight: '100vh',
          background: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, padding: '28px 36px' }}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default FinAppLayout;
